import { useTranslation } from "react-i18next";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopQuickComposer } from "../../../components/gpt/DesktopQuickComposer";
import { FeedEndAskGpt } from "../../../components/gpt/FeedEndAskGpt";
import { DesktopPracticeDropdown } from "../../../components/practice-feed/DesktopPracticeDropdown";
import { PracticeFeedRail } from "../../../components/practice-feed/PracticeFeedRail";
import {
  DesktopArticleHeroSkeleton,
  DesktopArticleRowSkeleton,
} from "../../../components/discovery/DesktopArticleRow";
import { DesktopExploreRail } from "../../../components/discovery/DesktopExploreRail";
import { DesktopCongressHero } from "../../../components/congress/DesktopCongressHero";
import { Button } from "../../../components/ui/Button";
import { SectionTitle } from "../../../components/ui/SectionTitle";
import {
  FeedItem,
  PracticeArticleFeedItem,
  PracticeCollectionsSection,
  PracticeFeedEmpty,
  PracticeRailViewed,
  RoomCollectionsSection,
  isItemNew,
} from "../PracticeFeedItems";
import { usePracticeFeedPage } from "../usePracticeFeedPage";
import {
  DesktopNewFeedPromo,
  useNewFeedPromoState,
} from "./DesktopNewFeedPromo";
import { useRecordRoomVisit } from "../../../domains/room/utils/roomVisits";

/**
 * The practice feed on a desktop browser (Figma "01 · Explore — Feed For you").
 *
 * Same data and same handlers as the mobile page (`usePracticeFeedPage`), drawn
 * as a top bar, a feed column opened by a hero, and a rail of rooms. An article
 * opens its own page.
 */
export function DesktopPracticeFeedPage(): React.JSX.Element {
  const { t } = useTranslation();
  const {
    scrollRef,
    rememberScroll,
    view,
    selection,
    selectedRoomId,
    filters,
    selectedPractice,
    selectedPracticeName,
    practices,
    rooms,
    roomFeed,
    railTerms,
    effectiveDescriptorUi,
    litTerm,
    practiceFeed,
    practiceArticles,
    feedArticles,
    handleSelectChip,
    handleChangeFilters,
    handleClearAll,
    handleSelectCorpus,
  } = usePracticeFeedPage();
  // The desktop rows carry no "New" mark, so opening a room's feed here is what
  // clears its badge, as the room page does.
  useRecordRoomVisit(selectedRoomId);
  const [newFeedPromo, setNewFeedPromo] = useNewFeedPromoState();

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <DesktopTopNav
        context={
          <DesktopPracticeDropdown
            practices={practices}
            corpus={view === null ? null : view.corpus}
            onSelectCorpus={handleSelectCorpus}
          />
        }
        trailing={{ kind: "search" }}
        surface="default"
        className={null}
      />
      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        <div
          ref={scrollRef}
          className="min-w-0 flex-1 overflow-y-auto"
          onScroll={(e) => {
            const el = e.currentTarget;
            rememberScroll();
            if (el.scrollTop + el.clientHeight < el.scrollHeight * 0.9) return;
            if (selectedRoomId !== null) {
              if (roomFeed.canFetchNextPage) roomFeed.fetchNextPage();
              return;
            }
            if (practiceFeed.state === "ready" && practiceFeed.canFetchNextPage)
              practiceFeed.fetchNextPage();
          }}
        >
          <div className="px-10 pt-8 pb-10">
            <div className="mx-auto flex max-w-desktop-block gap-8">
              <div className="flex min-w-0 max-w-desktop-column flex-1 flex-col gap-4">
                {view !== null && railTerms.status === "ready" && (
                  <PracticeRailViewed corpus={view.corpus} />
                )}
                <PracticeFeedRail
                  layout="desktop"
                  rail={railTerms}
                  rooms={rooms}
                  selection={selection}
                  onSelect={handleSelectChip}
                  filters={filters}
                  onChangeFilters={handleChangeFilters}
                  onClearAll={handleClearAll}
                  drawerCorpus={
                    view === null || selectedRoomId !== null
                      ? null
                      : view.corpus
                  }
                  practiceLabel={
                    selectedPracticeName === null ? "" : selectedPracticeName
                  }
                />

                {selectedRoomId === null && <DesktopCongressHero />}

                {selectedRoomId !== null ? (
                  <>
                    <RoomCollectionsSection
                      roomId={selectedRoomId}
                      layout="desktop"
                    />
                    <h2 className="m-0 text-heading-xs font-semibold text-ds-black">
                      {t("Room publications")}
                    </h2>
                    <div className="flex flex-col divide-y divide-ds-slate-100">
                      {roomFeed.isLoading ? (
                        Array.from({ length: 5 }).map((_, idx) => (
                          <DesktopArticleRowSkeleton key={idx} />
                        ))
                      ) : feedArticles.length === 0 ? (
                        <p className="py-6 text-center text-footnote text-ds-slate-500">
                          {t("No content to show right now.")}
                        </p>
                      ) : (
                        <>
                          {feedArticles.map((item) => (
                            <FeedItem
                              key={item.id}
                              item={item}
                              roomId={selectedRoomId}
                              surface="room"
                              isNew={isItemNew(item)}
                              layout={{ kind: "desktop" }}
                            />
                          ))}
                          {roomFeed.isFetchingNextPage &&
                            Array.from({ length: 2 }).map((_, idx) => (
                              <DesktopArticleRowSkeleton key={idx} />
                            ))}
                        </>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {view !== null && (
                      <PracticeCollectionsSection
                        corpus={view.corpus}
                        layout="desktop"
                      />
                    )}
                    {selectedPracticeName !== null && (
                      <h2 className="sr-only">{selectedPracticeName}</h2>
                    )}
                    <SectionTitle title={t("Publications")} />
                    {view === null || practiceFeed.state === "pending" ? (
                      // The feed's own shape: the hero, then rows.
                      <div className="flex flex-col divide-y divide-ds-slate-100">
                        <DesktopArticleHeroSkeleton />
                        {Array.from({ length: 4 }).map((_, idx) => (
                          <DesktopArticleRowSkeleton key={idx} />
                        ))}
                      </div>
                    ) : practiceFeed.state === "error" ? (
                      <PracticeFeedFailedWithRetry
                        onRetry={practiceFeed.retry}
                        isRetrying={practiceFeed.isRetrying}
                      />
                    ) : practiceArticles.length === 0 ? (
                      // One practice among several: the way out is the whole
                      // corpus. "All practices" itself, or a reader with one
                      // practice, has no wider corpus to go to. Narrowed by a
                      // chip or the drawer, the miss is the narrowing's, not
                      // the practice's.
                      view.corpus.kind === "practice" &&
                      effectiveDescriptorUi === null &&
                      filters.journalUids.length === 0 &&
                      filters.publicationTypes.length === 0 &&
                      practices.length > 1 &&
                      selectedPracticeName !== null ? (
                        <PracticeEmptyWithWayOut
                          practiceName={selectedPracticeName}
                          onShowAll={() => {
                            handleSelectCorpus({ kind: "all" });
                          }}
                        />
                      ) : (
                        <PracticeFeedEmpty />
                      )
                    ) : (
                      <div className="flex flex-col divide-y divide-ds-slate-100">
                        {practiceArticles.map((article, index) => (
                          <PracticeArticleFeedItem
                            key={article.id}
                            item={article}
                            corpus={view.corpus}
                            openedFrom={{ articles: practiceArticles, index }}
                            layout={{
                              kind: "desktop",
                              size: index === 0 ? "hero" : "row",
                            }}
                          />
                        ))}
                        {/* The next page, drawn in the list's own rows so the
                          reader sees more is coming at the foot of the feed. */}
                        {practiceFeed.isFetchingNextPage &&
                          Array.from({ length: 2 }).map((_, idx) => (
                            <DesktopArticleRowSkeleton key={idx} />
                          ))}
                      </div>
                    )}
                    {/* One practice only, once the last page is in: see the
                      mobile page for why. */}
                    {selectedPractice !== undefined &&
                      practiceFeed.state === "ready" &&
                      practiceArticles.length > 0 &&
                      !practiceFeed.hasNextPage &&
                      (effectiveDescriptorUi === null || litTerm !== null) && (
                        <FeedEndAskGpt
                          feedScope={{
                            kind: "practice",
                            uid: selectedPractice.uid,
                            descriptorUi: effectiveDescriptorUi,
                          }}
                          title={selectedPractice.name}
                          narrowing={litTerm === null ? null : litTerm.label}
                        />
                      )}
                  </>
                )}
              </div>
              <div className="hidden w-desktop-rail shrink-0 border-l border-ds-slate-100 px-6 lg:block">
                <div className="sticky top-8">
                  <DesktopExploreRail />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* The feed introduces itself once, in the Quick task's corner. */}
      {newFeedPromo === "unseen" ? (
        <DesktopNewFeedPromo
          practices={practices}
          onDismiss={() => {
            setNewFeedPromo("dismissed");
          }}
        />
      ) : (
        <DesktopQuickComposer />
      )}
    </div>
  );
}

/**
 * One practice holds nothing (Figma "D3.explore.pour-vous.empty"): the reader
 * is offered the whole corpus, one click away, rather than an empty column.
 */
function PracticeEmptyWithWayOut({
  practiceName,
  onShowAll,
}: {
  practiceName: string;
  onShowAll: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex max-w-120 flex-col items-center gap-2 pt-24 text-center">
      <h3 className="m-0 text-desktop-h3 font-semibold text-ds-slate-950">
        {t("Nothing new in {{practice}}", { practice: practiceName })}
      </h3>
      <p className="m-0 text-sm text-ds-slate-500">
        {t("No recent articles match this practice.")}{" "}
        {t("Your feed shows everything again when you switch back.")}
      </p>
      <Button
        type="button"
        variant="secondary"
        size="md"
        className="mt-2 text-footnote font-medium"
        onClick={onShowAll}
      >
        {t("Show all practices")}
      </Button>
    </div>
  );
}

/**
 * The feed could not be read (Figma "D3.explore.pour-vous.error"). The query
 * also asks again on focus and on reconnect; "Try again" is for the reader who
 * stays on the page.
 */
function PracticeFeedFailedWithRetry({
  onRetry,
  isRetrying,
}: {
  onRetry: () => void;
  isRetrying: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex max-w-120 flex-col items-center gap-2 pt-24 text-center">
      <h3 className="m-0 text-desktop-h3 font-semibold text-ds-slate-950">
        {t("Your feed couldn't load")}
      </h3>
      <p className="m-0 text-sm text-ds-slate-500">
        {t("Check your connection, then try again.")}{" "}
        {t("Saved articles and collections are still in your profile.")}
      </p>
      <Button
        type="button"
        variant="secondary"
        size="md"
        className="mt-2 text-footnote font-medium"
        disabled={isRetrying}
        onClick={onRetry}
      >
        {t("Try again")}
      </Button>
    </div>
  );
}
